import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useWin98Scope from '../hooks/useWin98Scope';
import Windows98SkyBackground from '../components/Windows98SkyBackground';

// True when running as an installed PWA (Android WebAPK, desktop install, or iOS standalone).
// Used so the installed app always opens into the app, regardless of what start_url the
// already-minted WebAPK baked in.
const isInstalledPWA = () =>
  (typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.navigator.standalone === true));
import {
  Sparkles,
  Shield,
  ClipboardList,
  CheckSquare,
  Camera,
  Users,
  LayoutDashboard,
  Wallet,
  Image as ImageIcon,
  Link2,
  Heart,
  ArrowRight,
  ExternalLink,
  Menu,
  X
} from 'lucide-react';

const MANDOMASTER_URL = 'https://mando.kybersuite.com';
const STARFORGE_URL = 'https://thestarforge.org';

// One-time "Setup Wizard" intro: a brief fake-install progress bar, then the
// hero headline types itself out. Plays once per browser tab (sessionStorage
// gated) and is skipped entirely under prefers-reduced-motion, same
// convention as the cloud-drift in Windows98SkyBackground.jsx.
const INTRO_SEEN_KEY = 'kc-landing-intro-seen';
const HEADLINE = 'Every cosplay,\nstart to finish.';
const FAKE_FILES = ['armor_textures.dll', 'budget_tracker.dll', 'con_schedule.ini', 'photo_gallery.dll'];
const INTRO_LOAD_MS = 1200;
const TYPE_CHAR_MS = 35;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const hasSeenIntro = () => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage.getItem(INTRO_SEEN_KEY) === '1';
  } catch {
    return false;
  }
};

const markIntroSeen = () => {
  try {
    window.sessionStorage.setItem(INTRO_SEEN_KEY, '1');
  } catch {
    // Private-mode storage can throw — non-fatal, worst case the intro replays.
  }
};

const PILLARS = [
  { icon: ClipboardList, title: 'Plan projects', body: 'Set a status, deadline, and budget for every build.' },
  { icon: CheckSquare, title: 'Track tasks', body: 'Break a build into steps and check them off as you go.' },
  { icon: Camera, title: 'Save the details', body: 'Keep receipts, hours, and progress photos together.' },
  { icon: Users, title: 'Share your work', body: 'Post to the community, collect likes, and climb the board.' },
];

const FEATURES = [
  { icon: LayoutDashboard, title: 'Project dashboard', body: 'See active builds, deadlines, and spend on one screen.' },
  { icon: CheckSquare, title: 'Task lists', body: 'Organize each build into tasks and watch progress fill in.' },
  { icon: Wallet, title: 'Budget and expenses', body: 'Log costs, scan receipts, and keep an eye on the total.' },
  { icon: ImageIcon, title: 'Photo galleries', body: 'Upload reference and progress shots for every project.' },
  { icon: Heart, title: 'Community feed', body: 'Browse shared builds, leave comments, and give likes.' },
  { icon: Link2, title: 'Creator page', body: 'Get a public links page for your socials and your shop.' },
];

// The one sanctioned "pop of color" action, repeated at each entry point
// (header, hero, closing CTA) — same action, multiple doors, not multiple
// accents. Everything else on the page is a plain .win-btn.
const AccentButton = ({ children, as = 'a', className = '', ...props }) => {
  const cls = `win-btn win-btn-accent inline-flex items-center justify-center gap-2 px-6 py-3 ${className}`;
  if (as === 'link') return <Link className={cls} {...props}>{children}</Link>;
  return <a className={cls} {...props}>{children}</a>;
};

const PlainButton = ({ children, as = 'a', className = '', ...props }) => {
  const cls = `win-btn inline-flex items-center justify-center gap-2 px-6 py-3 ${className}`;
  if (as === 'link') return <Link className={cls} {...props}>{children}</Link>;
  return <a className={cls} {...props}>{children}</a>;
};

const LandingPage = () => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [skipIntro] = useState(() => prefersReducedMotion() || hasSeenIntro());
  const [introPhase, setIntroPhase] = useState(() => (skipIntro ? 'done' : 'loading'));
  const [fileIdx, setFileIdx] = useState(0);
  const [typedLen, setTypedLen] = useState(() => (skipIntro ? HEADLINE.length : 0));
  useWin98Scope();

  // Loading phase: cycle the fake filename, then hand off to typing.
  useEffect(() => {
    if (introPhase !== 'loading') return undefined;
    const fileTimer = setInterval(() => setFileIdx((i) => (i + 1) % FAKE_FILES.length), 300);
    const doneTimer = setTimeout(() => {
      markIntroSeen();
      setIntroPhase('typing');
    }, INTRO_LOAD_MS);
    return () => {
      clearInterval(fileTimer);
      clearTimeout(doneTimer);
    };
  }, [introPhase]);

  // Typing phase: reveal the headline one character at a time.
  useEffect(() => {
    if (introPhase !== 'typing') return undefined;
    if (typedLen >= HEADLINE.length) {
      setIntroPhase('done');
      return undefined;
    }
    const t = setTimeout(() => setTypedLen((n) => n + 1), TYPE_CHAR_MS);
    return () => clearTimeout(t);
  }, [introPhase, typedLen]);

  // The homepage is for browser visitors. If the installed app ever opens here
  // (e.g. an old WebAPK whose start_url is still "/"), send it straight into the app.
  if (isInstalledPWA()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[var(--win-desktop)] text-[var(--win-text)]">
      {/* Nav */}
      <header className="win-raised sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2">
            <span className="w-9 h-9 flex items-center justify-center bg-[var(--win-titlebar-start)]">
              <Sparkles className="w-5 h-5 text-white" />
            </span>
            <span className="font-bold text-lg">KyberCosplay</span>
          </a>

          <nav className="hidden md:flex items-center gap-7 text-sm">
            <a href="#features" className="hover:underline">Features</a>
            <a href="#mandomaster" className="hover:underline">MandoMaster</a>
            <Link to="/explore" className="hover:underline">Community</Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <AccentButton as="link" to="/dashboard" className="px-5 py-2">Open app <ArrowRight className="w-4 h-4" /></AccentButton>
            ) : (
              <>
                <Link to="/login" className="text-sm font-semibold hover:underline">Log in</Link>
                <AccentButton as="link" to="/register" className="px-5 py-2">Sign up</AccentButton>
              </>
            )}
          </div>

          <button className="win-btn win-btn-icon md:hidden" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden win-divider md:hidden px-4 py-4 space-y-3 bg-[var(--win-face)]">
            <a href="#features" onClick={() => setMenuOpen(false)} className="block">Features</a>
            <a href="#mandomaster" onClick={() => setMenuOpen(false)} className="block">MandoMaster</a>
            <Link to="/explore" onClick={() => setMenuOpen(false)} className="block">Community</Link>
            <div className="flex gap-3 pt-2">
              {user ? (
                <AccentButton as="link" to="/dashboard" className="flex-1 px-4 py-2">Open app</AccentButton>
              ) : (
                <>
                  <PlainButton as="link" to="/login" className="flex-1 px-4 py-2">Log in</PlainButton>
                  <AccentButton as="link" to="/register" className="flex-1 px-4 py-2">Sign up</AccentButton>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main id="top">
        {/* Hero */}
        <section className="relative overflow-hidden px-4 sm:px-6 py-20 sm:py-28 text-center min-h-[560px] flex flex-col items-center justify-center">
          <Windows98SkyBackground className="absolute inset-0 z-0 w-full h-full" />
          {!skipIntro && (
            <style>{`
              @keyframes kc-progress-fill { from { width: 0%; } to { width: 100%; } }
              @keyframes kc-cursor-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
              @media (prefers-reduced-motion: no-preference) {
                .kc-progress-fill { animation: kc-progress-fill ${INTRO_LOAD_MS}ms linear forwards; }
                .kc-cursor { animation: kc-cursor-blink 1s steps(1) infinite; }
              }
            `}</style>
          )}

          {introPhase === 'loading' ? (
            <div className="relative z-20 win-window max-w-sm mx-auto text-left">
              <div className="win-titlebar">
                <span>KyberCosplay Setup</span>
              </div>
              <div className="p-5">
                <p className="text-sm mb-3">Installing KyberCosplay...</p>
                <div className="win-sunken h-4 overflow-hidden">
                  <div className="h-full bg-[var(--win-titlebar-start)] kc-progress-fill" />
                </div>
                <p className="mt-3 text-xs font-mono" style={{ color: 'var(--win-text-disabled)' }}>
                  Copying: {FAKE_FILES[fileIdx]}
                </p>
              </div>
            </div>
          ) : (
            <div className="relative z-10 win-window inline-block text-left max-w-2xl mx-auto">
              <div className="win-titlebar">
                <span>Welcome</span>
              </div>
              <div className="p-6 sm:p-8 text-center">
                <span className="win-well inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
                  <Sparkles className="w-3.5 h-3.5" /> Cosplay project manager
                </span>
                <h1 className="font-extrabold mt-5 leading-[1.05] whitespace-pre-line" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.25rem)' }}>
                  {HEADLINE.slice(0, typedLen)}
                  {!skipIntro && <span className="kc-cursor" aria-hidden="true">█</span>}
                </h1>
                <p className="mt-5 mx-auto max-w-2xl" style={{ fontSize: 'clamp(1rem, 2.2vw, 1.2rem)' }}>
                  Plan the build, track the budget, and log your photos. Design Mandalorian armor in MandoMaster and bring it straight in.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <AccentButton as="link" to={user ? '/dashboard' : '/register'}>
                    {user ? 'Open app' : 'Get started'} <ArrowRight className="w-4 h-4" />
                  </AccentButton>
                  <PlainButton as="link" to="/explore">Browse builds</PlainButton>
                </div>
                <p className="mt-4 text-xs font-mono">Free to use. Sign up takes a minute.</p>
              </div>
            </div>
          )}
        </section>

        {/* Pillars */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PILLARS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="win-raised p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-[var(--win-titlebar-start)]">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-lg">{title}</h3>
                <p className="mt-1.5 text-sm">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}>What you can do</h2>
            <p className="mt-2">One place for the whole build, from the first idea to the finished photos.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="win-raised p-6">
                <div className="win-well w-11 h-11 mb-4 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-lg">{title}</h3>
                <p className="mt-1.5 text-sm">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* MandoMaster spotlight */}
        <section id="mandomaster" className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="win-window grid lg:grid-cols-2">
            <div className="win-titlebar lg:col-span-2">
              <span>Powered by MandoMaster</span>
            </div>
            <div className="p-8 sm:p-10">
              <h2 className="font-bold mt-1" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)' }}>Design your Mandalorian in 3D</h2>
              <p className="mt-4 leading-relaxed">
                MandoMaster is a free armor builder. Pick your pieces, fit them to your body, and see a price estimate as you go. Export the build and it lands here as a project.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href={MANDOMASTER_URL} target="_blank" rel="noopener noreferrer" className="win-btn win-btn-accent inline-flex items-center gap-2 px-6 py-3">
                  <Shield className="w-4 h-4" /> Open MandoMaster
                </a>
                <PlainButton as="link" to="/explore">Browse Mando builds</PlainButton>
              </div>
            </div>
            <div className="win-sunken relative min-h-[300px] lg:min-h-full lg:m-4 overflow-hidden">
              <img
                src={`${process.env.PUBLIC_URL || ''}/mando-cosplay.jpg`}
                alt="A Mandalorian armor build photographed in the snow — designed with MandoMaster"
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <div className="win-window px-6 py-12 sm:py-16">
            <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}>Start your next build today</h2>
            <p className="mt-3">Create a free account and add your first project.</p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <AccentButton as="link" to={user ? '/dashboard' : '/register'}>
                {user ? 'Open app' : 'Create account'} <ArrowRight className="w-4 h-4" />
              </AccentButton>
              {!user && <PlainButton as="link" to="/login">Log in</PlainButton>}
            </div>
            <p className="mt-6 text-sm">
              Want a custom build or print made for you? <Link to="/commission" className="font-semibold underline">Request a commission</Link>.
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="win-raised">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-[var(--win-titlebar-start)]">
              <Sparkles className="w-4 h-4 text-white" />
            </span>
            <span className="font-semibold">KyberCosplay</span>
            <span className="text-sm">by</span>
            <a href={STARFORGE_URL} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline inline-flex items-center gap-1">
              The Star Forge <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <a href={MANDOMASTER_URL} target="_blank" rel="noopener noreferrer" className="hover:underline">MandoMaster</a>
            <Link to="/commission" className="hover:underline">Commissions</Link>
            <Link to="/explore" className="hover:underline">Community</Link>
            <Link to="/login" className="hover:underline">Log in</Link>
          </div>
          <p className="text-xs font-mono">© 2026 The Star Forge</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
